import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { adminAPI, TimeSeriesData, RevenueStats, UsageByTier } from '../../services/admin';

export default function AdminAnalyticsPage() {
  const { t } = useTranslation();
  const [period, setPeriod] = useState('30d');
  const [userGrowth, setUserGrowth] = useState<TimeSeriesData | null>(null);
  const [revenue, setRevenue] = useState<RevenueStats | null>(null);
  const [usage, setUsage] = useState<UsageByTier | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    const fetchData = async () => {
      setIsLoading(true);
      try {
        const [growthData, revenueData, usageData] = await Promise.all([
          adminAPI.getUserGrowth(period),
          adminAPI.getRevenue(period),
          adminAPI.getUsageByTier(period),
        ]);
        setUserGrowth(growthData);
        setRevenue(revenueData);
        setUsage(usageData);
      } catch (err) {
        console.error('Failed to fetch analytics:', err);
      } finally {
        setIsLoading(false);
      }
    };

    fetchData();
  }, [period]);

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600" />
      </div>
    );
  }

  return (
    <div>
      <div className="flex items-center justify-between mb-8">
        <div>
          <h1 className="text-2xl font-bold text-slate-900 dark:text-white">{t('admin.analytics')}</h1>
          <p className="text-slate-500 dark:text-slate-400 mt-1">{t('adminAnalytics.subtitle')}</p>
        </div>
        <select
          value={period}
          onChange={(e) => setPeriod(e.target.value)}
          className="px-4 py-2 bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-lg text-slate-900 dark:text-white"
        >
          <option value="7d">{t('common.last7Days')}</option>
          <option value="30d">{t('common.last30Days')}</option>
          <option value="90d">{t('common.last90Days')}</option>
        </select>
      </div>

      {/* User Growth Chart */}
      <div className="bg-white dark:bg-slate-800 rounded-xl border border-slate-200 dark:border-slate-700 p-6 mb-6">
        <h2 className="text-lg font-semibold text-slate-900 dark:text-white mb-4">
          {t('adminAnalytics.userGrowth')}
        </h2>
        <div className="h-64 flex items-end gap-2">
          {userGrowth?.datasets[0]?.data.map((value, index) => {
            const maxValue = Math.max(...(userGrowth.datasets[0]?.data || [1]));
            const height = (value / maxValue) * 100;
            return (
              <div key={index} className="flex-1 flex flex-col items-center gap-1">
                <div
                  className="w-full bg-blue-500 dark:bg-blue-600 rounded-t min-h-[4px]"
                  style={{ height: `${Math.max(height, 2)}%` }}
                />
                <span className="text-xs text-slate-500 dark:text-slate-400 truncate w-full text-center">
                  {userGrowth.labels[index]}
                </span>
              </div>
            );
          })}
        </div>
      </div>

      {/* Revenue and Usage */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6">
        {/* Revenue */}
        <div className="bg-white dark:bg-slate-800 rounded-xl border border-slate-200 dark:border-slate-700 p-6">
          <h2 className="text-lg font-semibold text-slate-900 dark:text-white mb-4">{t('adminDashboard.revenue')}</h2>
          <div className="space-y-4">
            <div className="flex justify-between items-center p-4 bg-slate-50 dark:bg-slate-700/50 rounded-lg">
              <span className="text-slate-600 dark:text-slate-400">MRR</span>
              <span className="text-2xl font-bold text-slate-900 dark:text-white">
                {revenue?.mrr ? `${(revenue.mrr / 100).toFixed(2)} PLN` : '0.00 PLN'}
              </span>
            </div>
            <div className="flex justify-between items-center p-4 bg-slate-50 dark:bg-slate-700/50 rounded-lg">
              <span className="text-slate-600 dark:text-slate-400">{t('adminDashboard.totalRevenue')}</span>
              <span className="text-xl font-semibold text-slate-700 dark:text-slate-300">
                {revenue?.totalRevenue ? `${(revenue.totalRevenue / 100).toFixed(2)} PLN` : '0.00 PLN'}
              </span>
            </div>
          </div>
        </div>

        {/* Usage by Tier */}
        <div className="bg-white dark:bg-slate-800 rounded-xl border border-slate-200 dark:border-slate-700 p-6">
          <h2 className="text-lg font-semibold text-slate-900 dark:text-white mb-4">
            {t('adminAnalytics.usageByTier')}
          </h2>
          <div className="space-y-4">
            <div className="p-4 bg-slate-50 dark:bg-slate-700/50 rounded-lg">
              <div className="flex justify-between items-center mb-2">
                <span className="text-slate-600 dark:text-slate-400">Free</span>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <p className="text-2xl font-bold text-slate-900 dark:text-white">
                    {usage?.free.searches || 0}
                  </p>
                  <p className="text-sm text-slate-500 dark:text-slate-400">{t('adminAnalytics.searches')}</p>
                </div>
                <div>
                  <p className="text-2xl font-bold text-slate-900 dark:text-white">
                    {usage?.free.analyses || 0}
                  </p>
                  <p className="text-sm text-slate-500 dark:text-slate-400">{t('adminAnalytics.analyses')}</p>
                </div>
              </div>
            </div>
            <div className="p-4 bg-purple-50 dark:bg-purple-900/20 rounded-lg">
              <div className="flex justify-between items-center mb-2">
                <span className="text-purple-700 dark:text-purple-300">Pro</span>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <p className="text-2xl font-bold text-purple-700 dark:text-purple-300">
                    {usage?.pro.searches || 0}
                  </p>
                  <p className="text-sm text-purple-600 dark:text-purple-400">{t('adminAnalytics.searches')}</p>
                </div>
                <div>
                  <p className="text-2xl font-bold text-purple-700 dark:text-purple-300">
                    {usage?.pro.analyses || 0}
                  </p>
                  <p className="text-sm text-purple-600 dark:text-purple-400">{t('adminAnalytics.analyses')}</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Usage Chart */}
      <div className="bg-white dark:bg-slate-800 rounded-xl border border-slate-200 dark:border-slate-700 p-6">
        <h2 className="text-lg font-semibold text-slate-900 dark:text-white mb-4">
          {t('adminAnalytics.usageOverTime')}
        </h2>
        <div className="h-64 flex items-end gap-2">
          {usage?.byDay?.datasets[0]?.data.map((value, index) => {
            const allData = usage.byDay.datasets.flatMap((d) => d.data);
            const maxValue = Math.max(...allData, 1);
            const height = (value / maxValue) * 100;
            const searchHeight = (usage.byDay.datasets[1]?.data[index] || 0) / maxValue * 100;
            return (
              <div key={index} className="flex-1 flex flex-col items-center gap-1">
                <div className="w-full flex flex-col gap-0.5" style={{ height: '100%' }}>
                  <div
                    className="w-full bg-green-500 dark:bg-green-600 rounded-t"
                    style={{ height: `${Math.max(height, 1)}%` }}
                  />
                  <div
                    className="w-full bg-blue-500 dark:bg-blue-600"
                    style={{ height: `${Math.max(searchHeight, 1)}%` }}
                  />
                </div>
                <span className="text-xs text-slate-500 dark:text-slate-400 truncate w-full text-center">
                  {usage.byDay.labels[index]}
                </span>
              </div>
            );
          })}
        </div>
        <div className="flex items-center justify-center gap-6 mt-4">
          <div className="flex items-center gap-2">
            <div className="w-3 h-3 bg-green-500 rounded" />
            <span className="text-sm text-slate-600 dark:text-slate-400">{t('adminAnalytics.analyses')}</span>
          </div>
          <div className="flex items-center gap-2">
            <div className="w-3 h-3 bg-blue-500 rounded" />
            <span className="text-sm text-slate-600 dark:text-slate-400">{t('adminAnalytics.searches')}</span>
          </div>
        </div>
      </div>
    </div>
  );
}
